import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { bpfAPI } from '../index';
import {
  CreateCloseReasonVariables,
  MutateCloseReasonsResponse,
  DeleteCloseReasonVariables,
  CreateCloseReasonsResponse,
  CreateCloseReasonsVariables,
  UpdateCloseReasonLabelVariables,
  UpdateCloseReasonStatusVariables,
} from './types';

export function useBPFCloseReasonsMutations() {
  const { showNotification } = useNotifications();

  const [onDeleteCloseReason, { loading: isDeleting }] = useMutation<
    MutateCloseReasonsResponse,
    DeleteCloseReasonVariables
  >(bpfAPI.deleteCloseReason, { refetchQueries: [bpfAPI.fetchCloseReasons], awaitRefetchQueries: true });

  const [onCreateCloseReasons] = useMutation<CreateCloseReasonsResponse, CreateCloseReasonsVariables>(
    bpfAPI.createCloseReasons
  );

  const [onCreateCloseReason, { loading: isCreating }] = useMutation<
    MutateCloseReasonsResponse,
    CreateCloseReasonVariables
  >(bpfAPI.createCloseReason, { refetchQueries: [bpfAPI.fetchCloseReasons], awaitRefetchQueries: true });

  const [onUpdateCloseReasonLabel, { loading: isLabelUpdating }] = useMutation<
    MutateCloseReasonsResponse,
    UpdateCloseReasonLabelVariables
  >(bpfAPI.updateCloseReasonLabel);

  const [onUpdateCloseReasonStatus, { loading: isStatusUpdating }] = useMutation<
    MutateCloseReasonsResponse,
    UpdateCloseReasonStatusVariables
  >(bpfAPI.updateCloseReasonStatus);

  const [onCloseDeal, { loading: isDealClosing }] = useMutation<MutateCloseReasonsResponse>(bpfAPI.closeDeal);

  const handleCreateCloseReasons = async (dto: CreateCloseReasonsVariables['input']) => {
    try {
      const { data, errors } = await onCreateCloseReasons({ variables: { input: dto } });
      if (errors?.length || data?.bpf?.createCloseReasons?.error || !data?.bpf?.createCloseReasons?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.createCloseReasons?.error ?? 'Something went wrong', { type: 'error' });
        return [];
      }

      showNotification('Reasons created successfully.');

      return data.bpf.createCloseReasons.entities;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return [];
    }
  };

  const handleCreateCloseReason = async (dto: CreateCloseReasonVariables['input']) => {
    try {
      const { data, errors } = await onCreateCloseReason({ variables: { input: dto } });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return [];
      }

      showNotification('Reason created successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleUpdateCloseReasonStatus = async (id: string, status: boolean) => {
    try {
      const { data, errors } = await onUpdateCloseReasonStatus({ variables: { id, status } });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return [];
      }

      showNotification('Close reason updated successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleUpdateCloseReasonLabel = async (id: string, label: string) => {
    try {
      const { data, errors } = await onUpdateCloseReasonLabel({ variables: { id, label } });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleDeleteCloseReason = async (id: string) => {
    try {
      const { data, errors } = await onDeleteCloseReason({ variables: { id } });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCreateCloseReasons error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      showNotification('Close reason deleted successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleCloseDeal = async (options: {
    stageId: string;
    parentId: string;
    reasonId: string;
    parentType: string;
    reasonStatus: 'SUCCESS' | 'FAILED';
  }) => {
    try {
      const { data, errors } = await onCloseDeal({ variables: { input: options } });

      if (errors?.length || data?.bpf?.result?.error || !data?.bpf?.result?.success) {
        logError('handleCloseDeal error');
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        return null;
      }

      showNotification('Deal closed successfully.');

      return data.bpf.result.entity;
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  return {
    loading: {
      isCreating,
      isDeleting,
      isDealClosing,
      isLabelUpdating,
      isStatusUpdating,
    },
    actions: {
      handleCloseDeal,
      handleCreateCloseReason,
      handleDeleteCloseReason,
      handleCreateCloseReasons,
      handleUpdateCloseReasonLabel,
      handleUpdateCloseReasonStatus,
    },
  };
}
